// src/App.js
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './authConfig';
import { setIdToken } from './utils/api';

import Dashboard from './pages/Dashboard';
import FileTransfers from './pages/FileTransfers';
import Upload from './pages/Upload';
import CredentialTransfers from './pages/CredentialTransfers';
import NewCredentialTransfer from './pages/NewCredentialTransfer';
import AdminFiles from './pages/AdminFiles';
import AdminCredentials from './pages/AdminCredentials';
import EmailTemplates from './pages/EmailTemplates';
import AdminPageTemplates from './pages/AdminPageTemplates';
import FileRequests from './pages/FileRequests';
import CredentialRequests from './pages/CredentialRequests';
import NewRequest from './pages/NewRequest';
import SubmitPage from './pages/SubmitPage';
import ReceivePage from './pages/ReceivePage';
import Layout from './components/Layout';
import Login from './pages/Login';
import AccessDenied from './pages/AccessDenied';

import './styles.css';

const msalInstance = new PublicClientApplication(msalConfig);
const ACCESS = { CHECKING: 'checking', UNAUTHENTICATED: 'unauthenticated', DENIED: 'denied', STAFF: 'staff', ADMIN: 'admin' };

function AppRoutes() {
  const [access, setAccess] = useState(ACCESS.CHECKING);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        await msalInstance.initialize();
        const redirectResult = await msalInstance.handleRedirectPromise();
        let account = redirectResult?.account || null;
        if (!account) { const accounts = msalInstance.getAllAccounts(); if (accounts.length > 0) account = accounts[0]; }
        if (account) msalInstance.setActiveAccount(account);
        if (!account || !account.idToken) { setAccess(ACCESS.UNAUTHENTICATED); return; }
        setIdToken(account.idToken);
        const res = await fetch('/api/auth/me', { headers: { 'X-ID-Token': account.idToken } });
        if (!res.ok) { setAccess(ACCESS.DENIED); return; }
        const data = await res.json();
        setUser({ name: account.name || data.name || account.username, email: data.email || account.username, userId: account.localAccountId });
        if (data.isAdmin) setAccess(ACCESS.ADMIN);
        else if (data.isStaff) setAccess(ACCESS.STAFF);
        else setAccess(ACCESS.DENIED);
      } catch (e) { console.error('Init error:', e); setAccess(ACCESS.UNAUTHENTICATED); }
    };
    init();
  }, []);

  const handleLogin = async () => {
    try { await msalInstance.loginRedirect({ ...loginRequest, extraQueryParameters: { scope: 'openid profile email' } }); }
    catch (e) { console.error(e); }
  };
  const handleLogout = async () => {
    setIdToken(null);
    try { await msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin }); }
    catch (e) { console.error(e); }
  };

  if (access === ACCESS.CHECKING) return <div className="loading-screen"><div className="spinner" /><p>Loading…</p></div>;
  if (access === ACCESS.UNAUTHENTICATED) return <Routes><Route path="/receive/:token" element={<ReceivePage />} /><Route path="/submit/:token" element={<SubmitPage />} /><Route path="*" element={<Login onLogin={handleLogin} />} /></Routes>;
  if (access === ACCESS.DENIED) return <Routes><Route path="/receive/:token" element={<ReceivePage />} /><Route path="/submit/:token" element={<SubmitPage />} /><Route path="*" element={<AccessDenied user={user} onLogout={handleLogout} />} /></Routes>;

  const isAdmin = access === ACCESS.ADMIN;
  const AdminOnly = ({ element }) => isAdmin ? element : <Navigate to="/" replace />;

  return (
    <Routes>
      <Route path="/receive/:token" element={<ReceivePage />} />
      <Route path="/submit/:token" element={<SubmitPage />} />
      <Route element={<Layout isAdmin={isAdmin} user={user} onLogout={handleLogout} />}>
        <Route path="/"                       element={<Dashboard isAdmin={isAdmin} />} />
        <Route path="/transfers"              element={<FileTransfers />} />
        <Route path="/upload"                 element={<Upload />} />
        <Route path="/credentials"            element={<CredentialTransfers />} />
        <Route path="/credentials/new"        element={<NewCredentialTransfer />} />
        <Route path="/filerequests"              element={<FileRequests />} />
        <Route path="/credentialrequests"        element={<CredentialRequests />} />
        <Route path="/filerequests/new"          element={<NewRequest type="file" />} />
        <Route path="/credentialrequests/new"    element={<NewRequest type="credential" />} />
        <Route path="/admin/files"            element={<AdminOnly element={<AdminFiles />} />} />
        <Route path="/admin/credentials"      element={<AdminOnly element={<AdminCredentials />} />} />
        <Route path="/admin/email-templates"  element={<AdminOnly element={<EmailTemplates />} />} />
        <Route path="/admin/page-templates"   element={<AdminOnly element={<AdminPageTemplates />} />} />
        <Route path="*"                       element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return <BrowserRouter><AppRoutes /></BrowserRouter>;
}
